import { useState } from 'react'
import toast from 'react-hot-toast'
import { assetApi } from '../services/assetFolderApi'
import type { MediaPreviewData } from '../components/MediaPreviewModal'

interface MomentPreviewSource {
  asset_id: number | null
  asset_file_type?: string | null
  asset_filename?: string | null
  thumbnail_url?: string | null
  title?: string | null
}

type PreviewKind = MediaPreviewData['type']
const TEXT_MIME_PREFIXES = ['text/']
const TEXT_MIME_EXACT = [
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-sh',
  'application/x-yaml',
]

function toPreviewKind(contentType?: string | null, fallbackType?: string | null): PreviewKind {
  const ct = (contentType || fallbackType || '').toLowerCase()
  if (ct.startsWith('video/')) return 'video'
  if (ct.startsWith('image/')) return 'image'
  if (ct.startsWith('audio/')) return 'audio'
  if (ct === 'application/pdf') return 'pdf'
  if (TEXT_MIME_PREFIXES.some(p => ct.startsWith(p)) || TEXT_MIME_EXACT.includes(ct)) return 'text'
  return 'file'
}

export function useMediaPreview() {
  const [preview, setPreview] = useState<MediaPreviewData | null>(null)

  const closePreview = () => setPreview(null)

  const openImage = (src: string, title?: string, subtitle?: string) => {
    setPreview({ type: 'image', src, title, subtitle })
  }

  const openVideo = (
    src: string,
    title?: string,
    subtitle?: string,
    opts?: { startTime?: number; endTime?: number },
  ) => {
    setPreview({ type: 'video', src, title, subtitle, startTime: opts?.startTime, endTime: opts?.endTime })
  }

  const openAudio = (src: string, title?: string, subtitle?: string) => {
    setPreview({ type: 'audio', src, title, subtitle })
  }

  const openPdf = (src: string, title?: string, subtitle?: string) => {
    setPreview({ type: 'pdf', src, title, subtitle })
  }

  const openFromAsset = async (opts: {
    assetId: number
    fileType?: string | null
    title?: string | null
    fallbackImageUrl?: string | null
    startTime?: number
    endTime?: number
  }) => {
    try {
      const res = await assetApi.getPreviewUrl(opts.assetId)
      const url = res.url
      const kind = toPreviewKind(res.content_type, opts.fileType)
      const title = opts.title || 'Preview'

      if (kind === 'image') {
        openImage(url, title)
        return
      }
      if (kind === 'video') {
        openVideo(url, title, undefined, { startTime: opts.startTime, endTime: opts.endTime })
        return
      }
      if (kind === 'audio') {
        openAudio(url, title)
        return
      }
      if (kind === 'pdf') {
        openPdf(url, title)
        return
      }
      if (kind === 'text') {
        try {
          const response = await fetch(url)
          const text = await response.text()
          setPreview({
            type: 'text',
            src: url,
            title,
            contentType: res.content_type,
            textContent: text.length > 200000 ? `${text.slice(0, 200000)}\n\n… Preview truncated …` : text,
          })
          return
        } catch {
          setPreview({ type: 'file', src: url, title, contentType: res.content_type })
          return
        }
      }
      setPreview({ type: 'file', src: url, title, contentType: res.content_type })
    } catch {
      if (opts.fallbackImageUrl) {
        openImage(opts.fallbackImageUrl, opts.title || 'Preview')
      } else {
        toast.error('Failed to load preview')
      }
    }
  }

  const openFromMoment = async (moment: MomentPreviewSource) => {
    const title = moment.title || moment.asset_filename || 'Preview'
    if (moment.asset_id) {
      await openFromAsset({
        assetId: moment.asset_id,
        fileType: moment.asset_file_type,
        title,
        fallbackImageUrl: moment.thumbnail_url || null,
      })
      return
    }

    if (moment.thumbnail_url) {
      openImage(moment.thumbnail_url, title)
      return
    }

    toast.error('No preview available for this item')
  }

  return {
    preview,
    closePreview,
    openImage,
    openVideo,
    openAudio,
    openPdf,
    openFromAsset,
    openFromMoment,
  }
}
